import { Button, IconButton, Spinner } from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';
import { MdFileUpload, MdPrint } from 'react-icons/md';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { InventoryReportStatusEnum } from 'types/report';
import { InventoryReportTypeEnum } from 'types/inventory';
import { HarvestSummaryPdfGeneratorProps } from './HarvestSummary.props';

const createDummyReport = (seed: any): any => {
  const random = (min: number, max: number) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  const skus = Array.from({ length: 20 }).map((_, i) => {
    const id = `sku-${i + 1}`;
    const pricePerReplacement = random(50, 200);
    const pricePerDelivery = random(1, 10);

    return {
      sku: {
        _id: id,
        name: `SKU-${String(i + 1).padStart(3, '0')}`,
        priority: i + 1,
        pricePerReplacement,
        pricePerDelivery,
      },
      // beginning, delivered, returned dirty, unused, ending
      beg: random(1000, 9999),
      del: random(200000, 900000), // Large 6-digit values for testing
      ret: random(200000, 900000), // Large 6-digit values for testing
      unu: random(0, 5000),
      end: random(1000, 9999),
    };
  });

  return {
    name: 'Dummy Report - Large Dataset Test',
    reportType: InventoryReportTypeEnum.NO_INVENTORY,
    status: InventoryReportStatusEnum.READY_FOR_IMPACT,
    totalOrders: random(10, 50),
    totalPickups: random(10, 50),
    customerDetails: { logo: '' },
    cokeAdjustment: random(5000, 50000),
    feeAdjustment: random(1000, 10000),
    site: {
      name: 'Dummy Site - Large Dataset',
      hasCokeAdjustment: true,
      pricingDetails: skus.slice(0, 10).map((r) => ({
        itemSku: r.sku._id,
        replacementPrice: r.sku.pricePerReplacement,
        deliveryPrice: r.sku.pricePerDelivery,
      })),
    },
    details: skus,
  };
};

const HarvestSummaryPdfGenerator = ({
  fileName,
  documentTemplate: DocumentTemplate,
  data,
  type = 'icon',
  secondFileName = '',
  secondDocumentTemplate: SecondDocumentTemplate,
  useDummyDataOverride,
}: HarvestSummaryPdfGeneratorProps) => {
  const [blobData, setBlobData] = useState<Blob | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [secondBlobData, setSecondBlobData] = useState<Blob | null>(null);
  const [loadingSecond, setLoadingSecond] = useState<boolean>(false);
  const [pdfReady, setPdfReady] = useState<boolean>(false);
  const [secondPdfReady, setSecondPdfReady] = useState<boolean>(false);

  // Use dummy data if environment variable is set or override is provided
  const effectiveData = useMemo(() => {
    const useDummy =
      useDummyDataOverride === true ||
      process.env.REACT_APP_USE_DUMMY_REUSE_REPORT === 'true';
    return useDummy ? createDummyReport(data) : data;
  }, [data, useDummyDataOverride]);

  const handleDownload = (blob: Blob | null, fileName: string) => {
    if (blob) {
      const link = document.createElement('a');
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }
  };

  // Effect to handle downloading the first PDF when ready
  useEffect(() => {
    if (loading && blobData) {
      handleDownload(blobData, fileName);
      setLoading(false);
    }
  }, [blobData, loading]);

  // Effect to handle downloading the second PDF when ready
  useEffect(() => {
    if (loadingSecond && secondBlobData) {
      handleDownload(secondBlobData, secondFileName);
      setLoadingSecond(false);
    }
  }, [secondBlobData, loadingSecond]);

  const startDownload = (type: 'first' | 'second') => {
    type === 'first' ? setLoading(true) : setLoadingSecond(true);
  };

  // Determines if both PDFs should be downloaded based on the report status
  const shouldDownloadBoth =
    data.status !== InventoryReportStatusEnum.IN_REVIEW && secondFileName;

  const showDownloadLink =
    loadingSecond &&
    shouldDownloadBoth &&
    SecondDocumentTemplate &&
    !secondBlobData;

  return (
    <>
      {loading || loadingSecond ? (
        <Spinner color="primary.500" size="sm" />
      ) : (
        <>
          {type === 'icon' && (
            <IconButton
              onClick={() => {
                if (pdfReady) {
                  handleDownload(blobData, fileName);
                  if (shouldDownloadBoth && secondPdfReady) {
                    handleDownload(secondBlobData, secondFileName);
                  }
                } else {
                  startDownload('first');
                  if (shouldDownloadBoth) {
                    startDownload('second');
                  }
                }
              }}
              size="xs"
              variant="ghost"
              color="support.500"
              _hover={{ color: 'primary.300' }}
              _active={{ color: 'primary.200' }}
              aria-label="print-report"
              icon={<MdPrint size="18px" />}
            />
          )}

          {type === 'button' && (
            <Button
              onClick={() => {
                if (pdfReady) {
                  handleDownload(blobData, fileName);
                  if (shouldDownloadBoth && secondPdfReady) {
                    handleDownload(secondBlobData, secondFileName);
                  }
                } else {
                  startDownload('first');
                  if (shouldDownloadBoth) {
                    startDownload('second');
                  }
                }
              }}
              leftIcon={<MdFileUpload />}>
              Export PDF
            </Button>
          )}
        </>
      )}

      {/* PDFDownloadLink for generating the first PDF */}
      {loading && !blobData && (
        <PDFDownloadLink
          document={<DocumentTemplate data={effectiveData} />}
          fileName={fileName}>
          {({ blob, loading }: { blob: Blob | null; loading: boolean }) => {
            if (!loading && blob && !blobData) {
              setBlobData(blob);
              setPdfReady(true);
            }
            return null; // Do not render any additional components
          }}
        </PDFDownloadLink>
      )}

      {/* PDFDownloadLink for generating the second PDF, if applicable */}
      {showDownloadLink && (
        <PDFDownloadLink
          document={<SecondDocumentTemplate data={effectiveData} />}
          fileName={secondFileName}>
          {({ blob, loading }: { blob: Blob | null; loading: boolean }) => {
            if (!loading && blob && !secondBlobData) {
              setSecondBlobData(blob);
              setSecondPdfReady(true);
            }
            return null;
          }}
        </PDFDownloadLink>
      )}
    </>
  );
};

export default HarvestSummaryPdfGenerator;
